import React, { useEffect } from 'react';
import { Monitor, Eye, Paintbrush, Printer, Layers, Accessibility, Sliders, Warning } from '../../components/icons';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { SwatchBook } from '../../components/icons';
import AdBanner from '../../components/AdBanner';
import { updateColorModelsMetadata } from '../../utils/seo';

const ColorModelsPage: React.FC = () => {
  useEffect(() => { updateColorModelsMetadata(); }, []);

  return (
    <Layout maxWidth="max-w-5xl">
      <PageHeader icon={SwatchBook} title="Color Models Guide" />
      <div className="ui-panel p-5 sm:p-8">
        
        <div className="ui-prose max-w-none text-ink-secondary">
          <p className="text-base text-ink-secondary mb-8 leading-relaxed">
            A color model is a mathematical system for describing colors. Different models are used for different purposes—some for screens (light), others for printing (ink), and some for intuitive human understanding.
          </p>

          {/* Section 1: Additive vs Subtractive */}
          <section className="mb-16 bg-surface p-6 rounded-xl border border-surface-border">
            <h2 className="text-2xl font-bold text-ink mb-6 flex items-center gap-2">
              <Layers className="text-brand-500" size={24} />
              Additive vs. Subtractive Mixing
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-ink mb-2">Additive (Light)</h3>
                <p className="text-sm mb-3">
                  Used by screens (monitors, phones). It starts with black (darkness) and adds Red, Green, and Blue light.
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li><strong>RGB</strong> is the primary additive model.</li>
                  <li>Adding all colors creates <strong>White</strong>.</li>
                  <li>Absence of all colors creates <strong>Black</strong>.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-ink mb-2">Subtractive (Ink)</h3>
                <p className="text-sm mb-3">
                  Used by printers. It starts with white (paper) and adds ink to subtract brightness.
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li><strong>CMYK</strong> is the primary subtractive model.</li>
                  <li>Adding all colors creates <strong>Black</strong> (muddy dark brown).</li>
                  <li>Absence of all colors creates <strong>White</strong> (paper).</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 2: Detailed Models */}
          <div className="space-y-12">
            {/* RGB */}
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-1/4 shrink-0">
                <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 text-center">
                  <Monitor size={32} className="mx-auto text-neutral-700 mb-2" />
                  <h3 className="font-bold text-ink">RGB</h3>
                  <span className="text-xs uppercase font-bold text-ink-muted">Red Green Blue</span>
                </div>
              </div>
              <div className="md:w-3/4">
                <h3 className="text-xl font-bold text-ink mb-2">The Digital Standard</h3>
                <p className="mb-4 text-sm leading-relaxed">
                  RGB is the native language of digital displays. Each pixel on your screen is made of a red, green, and blue sub-pixel. By varying the intensity of each (0-255), we can create over 16 million colors.
                </p>
                <div className="bg-surface-sunken p-3 rounded-lg text-xs font-mono text-ink-secondary">
                  rgb(255, 0, 0)   = Red<br/>
                  rgb(0, 255, 0)   = Green<br/>
                  rgb(0, 0, 255)   = Blue<br/>
                  rgb(255, 255, 255) = White
                </div>
              </div>
            </div>

            {/* CMYK */}
            <div className="flex flex-col md:flex-row gap-6 border-t border-surface-border pt-8">
              <div className="md:w-1/4 shrink-0">
                <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 text-center">
                  <Printer size={32} className="mx-auto text-neutral-700 mb-2" />
                  <h3 className="font-bold text-ink">CMYK</h3>
                  <span className="text-xs uppercase font-bold text-ink-muted">Cyan Magenta Yellow Key</span>
                </div>
              </div>
              <div className="md:w-3/4">
                <h3 className="text-xl font-bold text-ink mb-2">The Print Standard</h3>
                <p className="mb-4 text-sm leading-relaxed">
                  Printers use four ink plates. 'Key' stands for Black, because combining Cyan, Magenta, and Yellow produces a muddy brown, not a true black. Pure black ink is cheaper and sharper for text.
                </p>
                <p className="text-sm font-medium text-ink-secondary">
                  <Warning size={16} className="mr-1 inline-block align-text-bottom" /> Warning: Colors on screen (RGB) often look duller when printed (CMYK) because screens can emit light that ink cannot reflect.
                </p>
              </div>
            </div>

            {/* HSL */}
            <div className="flex flex-col md:flex-row gap-6 border-t border-surface-border pt-8">
              <div className="md:w-1/4 shrink-0">
                <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 text-center">
                  <Eye size={32} className="mx-auto text-neutral-700 mb-2" />
                  <h3 className="font-bold text-ink">HSL</h3>
                  <span className="text-xs uppercase font-bold text-ink-muted">Hue Saturation Lightness</span>
                </div>
              </div>
              <div className="md:w-3/4">
                <h3 className="text-xl font-bold text-ink mb-2">The Human Standard</h3>
                <p className="mb-4 text-sm leading-relaxed">
                  HSL is designed to be intuitive. Instead of mixing red, green, and blue, you pick a color (Hue), decide how vibrant it is (Saturation), and how bright it is (Lightness).
                </p>
                <ul className="list-disc pl-5 space-y-1 text-sm mb-4">
                  <li><strong>Hue (0-360°):</strong> Position on the color wheel. 0=Red, 120=Green, 240=Blue.</li>
                  <li><strong>Saturation (0-100%):</strong> 0% is gray, 100% is full color.</li>
                  <li><strong>Lightness (0-100%):</strong> 0% is black, 50% is pure color, 100% is white.</li>
                </ul>
                <p className="text-sm text-ink-secondary italic">
                  * Best for creating hover states (just lower the Lightness) or color palettes (keep Hue, vary Saturation/Lightness).
                </p>
              </div>
            </div>
          </div>

            {/* HSV */}
            <div className="flex flex-col md:flex-row gap-6 border-t border-surface-border pt-8">
              <div className="md:w-1/4 shrink-0">
                <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 text-center">
                  <Sliders size={32} className="mx-auto text-neutral-700 mb-2" />
                  <h3 className="font-bold text-ink">HSV</h3>
                  <span className="text-xs uppercase font-bold text-ink-muted">Hue Saturation Value</span>
                </div>
              </div>
              <div className="md:w-3/4">
                <h3 className="text-xl font-bold text-ink mb-2">The Artist's Standard</h3>
                <p className="mb-4 text-sm leading-relaxed">
                  HSV (also known as HSB — Hue, Saturation, Brightness) is similar to HSL but uses "Value" instead of "Lightness." The key difference: in HSV, maximum Value sets the largest RGB channel to its maximum. At full saturation this gives a vivid hue; at zero saturation it gives white. A color at 0 Value is black.
                </p>
                <ul className="list-disc pl-5 space-y-1 text-sm mb-4">
                  <li><strong>Hue (0–360°):</strong> The color angle on the color wheel. Same as in HSL.</li>
                  <li><strong>Saturation (0–100%):</strong> 0% is a neutral gray; 100% is a fully saturated hue.</li>
                  <li><strong>Value (0–100%):</strong> 0% is black; 100% is the full hue (or white if Saturation is also 0%).</li>
                </ul>
                <div className="bg-surface-sunken p-3 rounded-lg text-xs font-mono text-ink-secondary">
                  hsv(0°, 100%, 100%)   = Pure Red<br/>
                  hsv(0°, 0%, 100%)     = White<br/>
                  hsv(0°, 100%, 50%)    = Dark Red<br/>
                  hsv(0°, 0%, 0%)       = Black
                </div>
                <p className="text-sm text-ink-secondary italic mt-3">
                  * HSV is the model used in most image editing software color pickers (Photoshop, Figma, etc.) because it closely mirrors how artists mix paint: start with a pure hue and darken it by adding black.
                </p>
              </div>
            </div>

          <AdBanner slotId="8843219021" format="fluid" className="my-8" />

          {/* Section: When to Use Which Model */}
          <div className="mt-12 pt-8 border-t border-surface-border">
            <h2 className="text-2xl font-bold text-ink mb-6 flex items-center gap-2">
              <Paintbrush className="text-brand-500" size={24} />
              When to Use Which Model
            </h2>
            <p className="mb-6 text-sm text-ink-secondary">
              Each model is optimized for a different context. Choosing the right one saves time and prevents color inconsistencies across your workflow.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-surface-sunken text-left">
                    <th className="px-4 py-3 font-bold text-ink-secondary rounded-tl-lg">Model</th>
                    <th className="px-4 py-3 font-bold text-ink-secondary">Best For</th>
                    <th className="px-4 py-3 font-bold text-ink-secondary rounded-tr-lg">Avoid When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  <tr>
                    <td className="px-4 py-3 font-semibold text-ink">RGB</td>
                    <td className="px-4 py-3 text-ink-secondary">Defining exact screen colors in CSS and code</td>
                    <td className="px-4 py-3 text-ink-secondary">Designing for print output</td>
                  </tr>
                  <tr className="bg-surface">
                    <td className="px-4 py-3 font-semibold text-ink">CMYK</td>
                    <td className="px-4 py-3 text-ink-secondary">Preparing files for professional printing</td>
                    <td className="px-4 py-3 text-ink-secondary">Any screen-only design work</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-semibold text-ink">HSL</td>
                    <td className="px-4 py-3 text-ink-secondary">CSS variables, creating hover/active states, palette generation in code</td>
                    <td className="px-4 py-3 text-ink-secondary">When you need exact print color matching</td>
                  </tr>
                  <tr className="bg-surface">
                    <td className="px-4 py-3 font-semibold text-ink">HSV</td>
                    <td className="px-4 py-3 text-ink-secondary">Picking colors in Figma, Photoshop, or other visual tools</td>
                    <td className="px-4 py-3 text-ink-secondary">Specifying colors in CSS (use HSL instead)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-semibold text-ink">HEX</td>
                    <td className="px-4 py-3 text-ink-secondary">Sharing colors between developers, copy-pasting into code</td>
                    <td className="px-4 py-3 text-ink-secondary">When you need to adjust lightness or saturation programmatically</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <AdBanner slotId="8843219021" format="fluid" className="my-8" />

          {/* Section 3: Accessibility */}
          <div className="mt-16 pt-8 border-t border-surface-border">
            <h2 className="text-2xl font-bold text-ink mb-6 flex items-center gap-2">
              <Accessibility className="text-brand-500" size={24} />
              Web Accessibility (WCAG)
            </h2>
            <p className="mb-4">
              When choosing colors for the web, contrast is critical for users with visual impairments. The Web Content Accessibility Guidelines (WCAG) define specific contrast ratios.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h4 className="font-bold text-green-800 mb-1">AA Standard (Essential)</h4>
                <p className="text-sm text-green-700">
                  Requires a contrast ratio of at least <strong>4.5:1</strong> for normal text and <strong>3:1</strong> for large text.
                </p>
              </div>
              <div className="bg-brand-50 border border-brand-200 p-4 rounded-lg">
                <h4 className="font-bold text-brand-800 mb-1">AAA Standard (Ideal)</h4>
                <p className="text-sm text-brand-700">
                  Requires a contrast ratio of at least <strong>7:1</strong> for normal text and <strong>4.5:1</strong> for large text.
                </p>
              </div>
            </div>

            <div className="mt-8 bg-brand-50 border border-brand-100 rounded-xl p-5">
              <p className="text-sm text-brand-900">
                <strong>See these models in action.</strong> Every color in the <Link to="/" className="text-brand-700 hover:underline font-medium">Color Match tool</Link> displays its RGB, HSL, CMYK, and HSV values simultaneously. Explore <Link to="/theory" className="text-brand-700 hover:underline font-medium">Color Theory</Link> to learn how models relate to harmony and composition.
              </p>
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default ColorModelsPage;
